import React, { useState, useEffect, useCallback } from 'react';
import PageLoader from '../components/PageLoader';
import FeedbackModal from '../components/FeedbackModal';
import { api } from '../App';
import '../styles/AdminProductenPage.css';

function AdminProductenPage({ onBack, onLogout }) {
  const [groepen, setGroepen] = useState([]);
  const [activeGroepId, setActiveGroepId] = useState(null);
  const [producten, setProducten] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingProducten, setLoadingProducten] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const [newProduct, setNewProduct] = useState({ product: '', eenheid: '' });
  const [editingProduct, setEditingProduct] = useState(null);
  const [showNewGroep, setShowNewGroep] = useState(false);
  const [newGroep, setNewGroep] = useState({ naam: '', beschrijving: '' });
  const [editingGroep, setEditingGroep] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);

  const activeGroep = groepen.find((g) => g.ID === activeGroepId);
  const isAndersGroep = activeGroep?.naam?.toLowerCase() === 'anders';

  const loadProducten = useCallback(async (groepId) => {
    try {
      setLoadingProducten(true);
      setError('');
      const response = await api.get(`/index.php?action=adminproducten&groep_id=${groepId}`);
      setProducten(response.data);
      setLoadingProducten(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij laden van producten');
      setLoadingProducten(false);
    }
  }, []);

  const loadGroepen = useCallback(async (selectId = null) => {
    try {
      setError('');
      const response = await api.get('/index.php?action=adminproductgroepen');
      setGroepen(response.data);
      setLoading(false);

      if (selectId) {
        setActiveGroepId(selectId);
      } else {
        setActiveGroepId((current) => {
          if (current) return current;
          if (response.data.length === 0) return null;
          const first = response.data.find((g) => g.naam.toLowerCase() !== 'anders') || response.data[0];
          return first.ID;
        });
      }
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij laden van productgroepen');
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadGroepen();
  }, [loadGroepen]);

  useEffect(() => {
    if (activeGroepId) {
      loadProducten(activeGroepId);
    } else {
      setProducten([]);
    }
  }, [activeGroepId, loadProducten]);

  const handleAddProduct = async (e) => {
    e.preventDefault();
    if (!newProduct.product.trim()) return;

    try {
      setError('');
      await api.post('/index.php?action=adminproduct', {
        pgid: activeGroepId,
        product: newProduct.product.trim(),
        eenheid: newProduct.eenheid.trim()
      });
      setNewProduct({ product: '', eenheid: '' });
      setSuccess('Product toegevoegd');
      loadProducten(activeGroepId);
      loadGroepen(activeGroepId);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij toevoegen');
    }
  };

  const handleSaveProduct = async (e) => {
    e.preventDefault();
    if (!editingProduct) return;

    try {
      setError('');
      await api.put('/index.php?action=adminproduct', {
        id: editingProduct.ID,
        product: editingProduct.product.trim(),
        eenheid: editingProduct.eenheid.trim()
      });
      setEditingProduct(null);
      setSuccess('Product bijgewerkt');
      loadProducten(activeGroepId);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij opslaan');
    }
  };

  const handleDeleteProduct = async (id) => {
    try {
      setError('');
      await api.delete(`/index.php?action=adminproduct&id=${id}`);
      setConfirmDelete(null);
      setSuccess('Product verwijderd');
      loadProducten(activeGroepId);
      loadGroepen(activeGroepId);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij verwijderen');
      setConfirmDelete(null);
    }
  };

  const handleAddGroep = async (e) => {
    e.preventDefault();
    if (!newGroep.naam.trim()) return;

    try {
      setError('');
      const response = await api.post('/index.php?action=adminproductgroep', {
        naam: newGroep.naam.trim(),
        beschrijving: newGroep.beschrijving.trim()
      });
      setNewGroep({ naam: '', beschrijving: '' });
      setShowNewGroep(false);
      setSuccess('Productgroep toegevoegd');
      loadGroepen(response.data.id);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij toevoegen groep');
    }
  };

  const handleSaveGroep = async (e) => {
    e.preventDefault();
    if (!editingGroep) return;

    try {
      setError('');
      await api.put('/index.php?action=adminproductgroep', {
        id: editingGroep.ID,
        naam: editingGroep.naam.trim(),
        beschrijving: editingGroep.beschrijving.trim()
      });
      setEditingGroep(null);
      setSuccess('Productgroep bijgewerkt');
      loadGroepen(activeGroepId);
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij opslaan groep');
    }
  };

  const handleDeleteGroep = async (id) => {
    try {
      setError('');
      await api.delete(`/index.php?action=adminproductgroep&id=${id}`);
      setConfirmDelete(null);
      setSuccess('Productgroep verwijderd');
      setActiveGroepId(null);
      loadGroepen();
    } catch (err) {
      setError(err.response?.data?.message || 'Fout bij verwijderen groep');
      setConfirmDelete(null);
    }
  };

  if (loading) {
    return <PageLoader />;
  }

  return (
    <div className="admin-producten-container">
      {confirmDelete && (
        <FeedbackModal
          title={confirmDelete.type === 'product' ? 'Product verwijderen?' : 'Productgroep verwijderen?'}
          message={confirmDelete.message}
          onConfirm={() => {
            if (confirmDelete.type === 'product') {
              handleDeleteProduct(confirmDelete.id);
            } else {
              handleDeleteGroep(confirmDelete.id);
            }
          }}
          onCancel={() => setConfirmDelete(null)}
          confirmLabel="Verwijderen"
          cancelLabel="Annuleren"
        />
      )}

      {success && (
        <FeedbackModal
          title="Gelukt"
          message={success}
          onConfirm={() => setSuccess('')}
          confirmLabel="Ok"
        />
      )}

      <header className="admin-header">
        <button type="button" onClick={onBack} className="back-button">← Terug</button>
        <h1>Productcatalogus</h1>
        <button type="button" onClick={onLogout} className="logout-button">Uitloggen</button>
      </header>

      <main className="admin-main">
        <div className="admin-content admin-producten-content">
          {error && <div className="error-message">{error}</div>}

          <div className="catalogus-layout">
            <aside className="groepen-panel">
              <div className="panel-header">
                <h2>Productgroepen</h2>
                <button
                  type="button"
                  className="small-btn"
                  onClick={() => setShowNewGroep(!showNewGroep)}
                >
                  {showNewGroep ? 'Annuleren' : '+ Groep'}
                </button>
              </div>

              {showNewGroep && (
                <form className="inline-form" onSubmit={handleAddGroep}>
                  <input
                    type="text"
                    placeholder="Naam groep"
                    value={newGroep.naam}
                    onChange={(e) => setNewGroep({ ...newGroep, naam: e.target.value })}
                    required
                  />
                  <input
                    type="text"
                    placeholder="Beschrijving (optioneel)"
                    value={newGroep.beschrijving}
                    onChange={(e) => setNewGroep({ ...newGroep, beschrijving: e.target.value })}
                  />
                  <button type="submit" className="primary-btn">Toevoegen</button>
                </form>
              )}

              <ul className="groepen-list">
                {groepen.map((groep) => (
                  <li key={groep.ID}>
                    <button
                      type="button"
                      className={`groep-item ${activeGroepId === groep.ID ? 'active' : ''}`}
                      onClick={() => {
                        setActiveGroepId(groep.ID);
                        setEditingProduct(null);
                        setEditingGroep(null);
                      }}
                    >
                      <span className="groep-naam">{groep.naam}</span>
                      <span className="groep-count">{groep.aantal_producten}</span>
                    </button>
                  </li>
                ))}
              </ul>
            </aside>

            <section className="producten-panel">
              {activeGroep ? (
                <>
                  <div className="panel-header">
                    <div>
                      <h2>{activeGroep.naam}</h2>
                      {activeGroep.beschrijving && (
                        <p className="groep-beschrijving">{activeGroep.beschrijving}</p>
                      )}
                    </div>
                    <div className="groep-actions">
                      <button
                        type="button"
                        className="small-btn"
                        onClick={() =>
                          setEditingGroep({
                            ID: activeGroep.ID,
                            naam: activeGroep.naam,
                            beschrijving: activeGroep.beschrijving || ''
                          })
                        }
                      >
                        Groep bewerken
                      </button>
                      {activeGroep.naam.toLowerCase() !== 'anders' && (
                        <button
                          type="button"
                          className="small-btn danger-btn"
                          onClick={() =>
                            setConfirmDelete({
                              type: 'groep',
                              id: activeGroep.ID,
                              message: `Weet je zeker dat je "${activeGroep.naam}" wilt verwijderen?`
                            })
                          }
                        >
                          Groep verwijderen
                        </button>
                      )}
                    </div>
                  </div>

                  {editingGroep && (
                    <form className="edit-box" onSubmit={handleSaveGroep}>
                      <h3>Groep bewerken</h3>
                      <input
                        type="text"
                        value={editingGroep.naam}
                        onChange={(e) => setEditingGroep({ ...editingGroep, naam: e.target.value })}
                        required
                      />
                      <input
                        type="text"
                        placeholder="Beschrijving"
                        value={editingGroep.beschrijving}
                        onChange={(e) =>
                          setEditingGroep({ ...editingGroep, beschrijving: e.target.value })
                        }
                      />
                      <div className="form-actions">
                        <button type="button" className="secondary-btn" onClick={() => setEditingGroep(null)}>
                          Annuleren
                        </button>
                        <button type="submit" className="primary-btn">Opslaan</button>
                      </div>
                    </form>
                  )}

                  {isAndersGroep ? (
                    <p className="info-text">
                      De groep &quot;Anders&quot; gebruikt vrije productinvoer door bestellers. Je hoeft hier
                      geen standaardproducten toe te voegen.
                    </p>
                  ) : (
                    <>
                      <form className="add-product-form" onSubmit={handleAddProduct}>
                        <h3>Nieuw product</h3>
                        <div className="add-product-fields">
                          <input
                            type="text"
                            placeholder="Productnaam *"
                            value={newProduct.product}
                            onChange={(e) => setNewProduct({ ...newProduct, product: e.target.value })}
                            required
                          />
                          <input
                            type="text"
                            placeholder="Eenheid (bijv. stuks, kg)"
                            value={newProduct.eenheid}
                            onChange={(e) => setNewProduct({ ...newProduct, eenheid: e.target.value })}
                          />
                          <button type="submit" className="primary-btn">Toevoegen</button>
                        </div>
                      </form>

                      {loadingProducten ? (
                        <p className="loading-text">Producten laden...</p>
                      ) : producten.length === 0 ? (
                        <p className="info-text">Nog geen producten in deze groep.</p>
                      ) : (
                        <table className="producten-table">
                          <thead>
                            <tr>
                              <th>Product</th>
                              <th>Eenheid</th>
                              <th>Acties</th>
                            </tr>
                          </thead>
                          <tbody>
                            {producten.map((prod) =>
                              editingProduct?.ID === prod.ID ? (
                                <tr key={prod.ID} className="editing-row">
                                  <td colSpan={3}>
                                    <form className="inline-edit-form" onSubmit={handleSaveProduct}>
                                      <input
                                        type="text"
                                        value={editingProduct.product}
                                        onChange={(e) =>
                                          setEditingProduct({ ...editingProduct, product: e.target.value })
                                        }
                                        required
                                      />
                                      <input
                                        type="text"
                                        placeholder="Eenheid"
                                        value={editingProduct.eenheid || ''}
                                        onChange={(e) =>
                                          setEditingProduct({ ...editingProduct, eenheid: e.target.value })
                                        }
                                      />
                                      <button type="submit" className="primary-btn">Opslaan</button>
                                      <button
                                        type="button"
                                        className="secondary-btn"
                                        onClick={() => setEditingProduct(null)}
                                      >
                                        Annuleren
                                      </button>
                                    </form>
                                  </td>
                                </tr>
                              ) : (
                                <tr key={prod.ID}>
                                  <td>{prod.product}</td>
                                  <td>{prod.eenheid || '-'}</td>
                                  <td className="actions-cell">
                                    <button
                                      type="button"
                                      className="small-btn"
                                      onClick={() =>
                                        setEditingProduct({
                                          ID: prod.ID,
                                          product: prod.product,
                                          eenheid: prod.eenheid || ''
                                        })
                                      }
                                    >
                                      Bewerken
                                    </button>
                                    <button
                                      type="button"
                                      className="small-btn danger-btn"
                                      onClick={() =>
                                        setConfirmDelete({
                                          type: 'product',
                                          id: prod.ID,
                                          message: `Weet je zeker dat je "${prod.product}" wilt verwijderen?`
                                        })
                                      }
                                    >
                                      Verwijderen
                                    </button>
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      )}
                    </>
                  )}
                </>
              ) : (
                <p className="info-text">Selecteer een productgroep.</p>
              )}
            </section>
          </div>
        </div>
      </main>
    </div>
  );
}

export default AdminProductenPage;
